import { Card } from "@/components/ui/card";
import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

const ProblemSolution = () => {
  const { t } = useTranslation();

  const problems = t("pages.home.problemSolution.problems", { returnObjects: true }) as string[];
  const solutions = t("pages.home.problemSolution.solutions", { returnObjects: true }) as string[];

  return (
    <section id="problem-solution" className="section-padding bg-gradient-to-br from-accent/20 to-background relative overflow-hidden">
      <div className="container-custom">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        </div>

        <div className="text-center space-y-6 mb-16 sm:mb-20 relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-medium text-sm mb-4">
            {t("pages.home.problemSolution.badge")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-foreground font-bold leading-tight">
            {t("pages.home.problemSolution.title")}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            {t("pages.home.problemSolution.subtitle")}
          </p>

          <div className="flex justify-center mt-8">
            <div className="w-20 h-1 bg-gradient-to-r from-destructive via-muted to-success rounded-full"></div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
          <Card className="p-6 sm:p-8 border-destructive/20 bg-destructive/5 hover-lift h-full group transition-all duration-500 hover:shadow-2xl hover:shadow-destructive/10 hover:-translate-y-2 touch-manipulation active:scale-[0.98]">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl text-foreground mb-2 font-semibold">
                  {t("pages.home.problemSolution.problemsTitle")}
                </h3>
                <div className="w-12 h-1 bg-destructive mx-auto rounded-full" />
              </div>

              <div className="space-y-4">
                {problems.map((problem, index) => (
                  <div key={index} className="flex items-start space-x-3 group/item">
                    <div className="flex-shrink-0 w-6 h-6 bg-destructive/20 rounded-full flex items-center justify-center mt-0.5 transition-all duration-200 group-hover/item:bg-destructive/30">
                      <X className="w-4 h-4 text-destructive" />
                    </div>
                    <span className="text-muted-foreground font-medium transition-colors duration-200 group-hover/item:text-foreground">{problem}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-6 sm:p-8 border-success/20 bg-success/5 hover-lift h-full group transition-all duration-500 hover:shadow-2xl hover:shadow-success/10 hover:-translate-y-2 touch-manipulation active:scale-[0.98]">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl text-foreground mb-2 font-semibold">
                  {t("pages.home.problemSolution.solutionsTitle")}
                </h3>
                <div className="w-12 h-1 bg-success mx-auto rounded-full" />
              </div>

              <div className="space-y-4">
                {solutions.map((solution, index) => (
                  <div key={index} className="flex items-start space-x-3 group/item">
                    <div className="flex-shrink-0 w-6 h-6 bg-success/20 rounded-full flex items-center justify-center mt-0.5 transition-all duration-200 group-hover/item:bg-success/30">
                      <Check className="w-4 h-4 text-success" />
                    </div>
                    <span className="text-muted-foreground font-medium transition-colors duration-200 group-hover/item:text-foreground">{solution}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
